import {useEffect, useMemo, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {createPortal} from "react-dom";
import type {Category, SubSubcategory, Subcategory} from "@corporatesclub/shared";
import {paths} from "@/lib/paths";
import {getCategoriesCached} from "@/lib/shopCache";
import {useI18n} from "./I18nProvider";
import {LocaleSwitcher} from "./LocaleSwitcher";
import {useMobileMenu} from "./MobileMenuProvider";
import {
  BEAUTY_DEPARTMENT_ID,
  HOME_DEPARTMENTS,
  type HomeDepartmentConfig
} from "./homeDepartments";

type CatNode = {
  id: number;
  nume: string;
  url: string;
  children: CatNode[];
};

type Panel =
  | {kind: "root"}
  | {kind: "departments"}
  | {kind: "comingSoon"; dept: HomeDepartmentConfig}
  | {kind: "beautyCategories"; title: string; viewAllUrl?: string}
  | {kind: "nodes"; title: string; viewAllUrl?: string; nodes: CatNode[]};

function toNodesFromCategory(c: Category): CatNode {
  return {
    id: c.id,
    nume: c.nume,
    url: c.url,
    children: (c.subcategorii || []).map(toNodesFromSub)
  };
}

function toNodesFromSub(sc: Subcategory): CatNode {
  return {
    id: sc.id,
    nume: sc.nume,
    url: sc.url,
    children: (sc.subsubcategorii || []).map(toNodesFromSubSub)
  };
}

function toNodesFromSubSub(ssc: SubSubcategory): CatNode {
  return {
    id: ssc.id,
    nume: ssc.nume,
    url: ssc.url,
    children: (ssc.subsubsubcategorii || []).map((x) => ({
      id: x.id,
      nume: x.nume,
      url: x.url,
      children: []
    }))
  };
}

export function MobileMenu() {
  const {t} = useI18n();
  const {pathname} = useLocation();
  const {isOpen, close} = useMobileMenu();
  const [stack, setStack] = useState<Panel[]>([{kind: "root"}]);
  const [categories, setCategories] = useState<Category[]>([]);
  const rootNodes = useMemo(() => categories.map(toNodesFromCategory), [categories]);
  const panel = stack[stack.length - 1] ?? {kind: "root" as const};
  const canBack = stack.length > 1;

  function push(next: Panel) {
    setStack((s) => [...s, next]);
  }
  function pop() {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }
  function resetStack() {
    setStack([{kind: "root"}]);
  }

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    if(!isOpen) {
      resetStack();
      return;
    }
    getCategoriesCached().then(setCategories).catch(() => setCategories([]));
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("cc-mobileMenuOpen");
    function onKey(e: KeyboardEvent) {
      if(e.key !== "Escape") return;
      setStack((s) => {
        if(s.length > 1) return s.slice(0, -1);
        close();
        return s;
      });
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("cc-mobileMenuOpen");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  function openDepartment(dept: HomeDepartmentConfig) {
    if(dept.status !== "active" || dept.id !== BEAUTY_DEPARTMENT_ID) {
      push({kind: "comingSoon", dept});
      return;
    }
    push({
      kind: "beautyCategories",
      title: t(dept.nameKey),
      viewAllUrl: dept.hubPath
    });
  }

  function openNode(node: CatNode) {
    if(node.children.length) {
      push({
        kind: "nodes",
        title: node.nume,
        viewAllUrl: node.url,
        nodes: node.children
      });
      return;
    }
    close();
  }

  const title = (() => {
    if(panel.kind === "root") return t("Menu");
    if(panel.kind === "departments") return t("Departments");
    if(panel.kind === "comingSoon") return t(panel.dept.nameKey);
    if(panel.kind === "beautyCategories") return panel.title;
    return panel.title;
  })();

  if(typeof document === "undefined" || !isOpen) return null;

  return createPortal(
    <div className="cc-mobileMenu" role="dialog" aria-modal="true" aria-label={t("Menu")}>
      <button type="button" className="cc-mobileMenu__backdrop" aria-label={t("Close")} onClick={close} />
      <aside className="cc-mobileMenu__panel">
        <header className={`cc-mobileMenu__head${canBack ? " hasBack" : ""}`}>
          {canBack ? (
            <button type="button" className="cc-mobileMenu__back" onClick={pop} aria-label={t("Back")}>
              <i className="fa fa-chevron-left" aria-hidden="true" />
              <span>{t("Back")}</span>
            </button>
          ) : null}
          <h2 className="cc-mobileMenu__title">{title}</h2>
          <button type="button" className="cc-mobileMenu__close" onClick={close} aria-label={t("Close")}>
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>

        {panel.kind === "root" ? (
          <>
            <div className="cc-mobileMenu__locale">
              <p className="cc-mobileMenu__localeLabel">{t("Language")} / {t("Currency")}</p>
              <LocaleSwitcher />
            </div>
            <nav className="cc-mobileMenu__nav" aria-label={t("Menu")}>
              <ul>
                <li>
                  <button type="button" className="cc-mobileMenu__link" onClick={() => push({kind: "departments"})}>
                    <span>{t("Departments")}</span>
                    <i className="fa fa-chevron-right" aria-hidden="true" />
                  </button>
                </li>
                <li><Link to={paths.promotions} className="cc-mobileMenu__link" onClick={close}>{t("Discounts")}</Link></li>
                <li><Link to={paths.bestSellers} className="cc-mobileMenu__link" onClick={close}>{t("Best Sellers")}</Link></li>
                <li><Link to={paths.freeShipping} className="cc-mobileMenu__link" onClick={close}>{t("Free shipping")}</Link></li>
                <li><Link to={paths.brands} className="cc-mobileMenu__link" onClick={close}>{t("Brands")}</Link></li>
                <li><Link to={paths.new} className="cc-mobileMenu__link" onClick={close}>{t("New")}</Link></li>
                <li><Link to={paths.account} className="cc-mobileMenu__link" onClick={close}>{t("Account")}</Link></li>
                <li><Link to={paths.wishlist} className="cc-mobileMenu__link" onClick={close}>{t("Wishlist")}</Link></li>
              </ul>
            </nav>
          </>
        ) : null}

        {panel.kind === "departments" ? (
          <div className="cc-mobileMenu__depts">
            {HOME_DEPARTMENTS.map((dept) => {
              const soon = dept.status === "coming_soon";
              return (
                <button
                  key={dept.id}
                  type="button"
                  className={`cc-mobileMenu__deptCard${soon ? " isSoon" : ""}`}
                  style={{backgroundColor: dept.cardBg}}
                  onClick={() => openDepartment(dept)}
                >
                  <span className="cc-mobileMenu__deptCopy">
                    <span className="cc-mobileMenu__deptName">{t(dept.nameKey)}</span>
                    {soon ? (
                      <span className="cc-mobileMenu__deptBadge">{t("Home departments coming soon")}</span>
                    ) : (
                      <span className="cc-mobileMenu__deptHint">{t("See all")}</span>
                    )}
                  </span>
                  <img src={dept.cardImage} alt="" className="cc-mobileMenu__deptImg" loading="lazy" decoding="async" />
                  <i className="fa fa-chevron-right cc-mobileMenu__deptChevron" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        ) : null}

        {panel.kind === "comingSoon" ? (
          <div className="cc-mobileMenu__soon">
            <span className="cc-mobileMenu__deptBadge">{t("Home departments coming soon")}</span>
            <h3 className="cc-mobileMenu__soonTitle">{t(panel.dept.nameKey)}</h3>
            <p className="cc-mobileMenu__soonText">{t(panel.dept.descriptionKey)}</p>
          </div>
        ) : null}

        {panel.kind === "beautyCategories" || panel.kind === "nodes" ? (
          <div className="cc-mobileMenu__cats">
            {panel.viewAllUrl ? (
              <Link to={panel.viewAllUrl} className="cc-mobileMenu__viewAll" onClick={close}>
                <span>{t("See all")}</span>
                <i className="fa fa-arrow-right" aria-hidden="true" />
              </Link>
            ) : null}
            {panel.kind === "beautyCategories" && !rootNodes.length ? (
              <p className="cc-mobileMenu__loading">{t("Loading products...")}</p>
            ) : (
              <ul>
                {(panel.kind === "beautyCategories" ? rootNodes : panel.nodes).map((node) => (
                  <li key={node.id}>
                    {node.children.length ? (
                      <button type="button" className="cc-mobileMenu__link" onClick={() => openNode(node)}>
                        <span>{node.nume}</span>
                        <i className="fa fa-chevron-right" aria-hidden="true" />
                      </button>
                    ) : (
                      <Link to={node.url} className="cc-mobileMenu__link" onClick={close}>
                        <span>{node.nume}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </aside>
    </div>,
    document.body
  );
}
